import { DB_TYPE } from '@/common/define/common.define';
import {
  getGetV7MysqlSqlTextQueryKey,
  getV7MysqlSqlText,
} from '@/api-client/mysql/queries/sql-stat-v7-controller/sql-stat-v7-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { formatTimeRange } from '@/database/utils/format.utils';
import { DATABASE_DETAIL_TYPE, DOCUMENT_TEXT_TYPE } from '@/database/adapter/openDetail.define';
import type {
  DetailParams,
  ParameterDetailParams,
  SessionDetailParams,
  SqlDetailParams,
  DocumentTextParams,
} from '../types';
import { formatName, mergeDetailInfo, parseSchema } from './utils';
import {
  sessionDetailSchema,
  sqlDetailSchema,
  slowQuerySchema,
  objectDetailSchema,
  parameterDetailSchema,
  documentTextSchema,
  type SessionDetailSchema,
  type MysqlSessionDetailAddition,
  type SqlDetailSchema,
  type MysqlSqlDetailAddition,
  type SlowQuerySchema,
  type MysqlSlowQueryAddition,
  type ObjectDetailSchema,
  type MysqlObjectDetailAddition,
  type ParameterDetailSchema,
  type MysqlParameterDetailAddition,
  type DocumentTextSchema,
} from './mysql.schema';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';

export const getMysqlSessionDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  isOpenedFromPA,
  init,
  instanceId,
  lockType,
  isSlowQuery,
}: SessionDetailParams): { name: string; addition: MysqlSessionDetailAddition } => {
  const { data } = parseSchema<SessionDetailSchema>(
    sessionDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime, isOpenedFromPA, init }),
    DATABASE_DETAIL_TYPE.SESSION,
  );

  return {
    name: formatName(data.threadId),
    addition: {
      dbType: DB_TYPE.MYSQL,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      threadId: data.threadId,
      lockType,
      isSlowQuery,
      isOpenedFromPA: data.isOpenedFromPA,
      init: data.init,
    },
  };
};

export const getMysqlSqlDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: SqlDetailParams): { name: string; addition: MysqlSqlDetailAddition } => {
  const { data } = parseSchema<SqlDetailSchema>(
    sqlDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.SQL,
  );

  return {
    name: formatName(data.digest),
    addition: {
      dbType: DB_TYPE.MYSQL,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      digest: data.digest,
      schema: data.db,
      sqlId: data.sqlId,
      sqlTextHashId: data.sqlTextHashId,
    },
  };
};

export const getMysqlSlowQueryAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: DetailParams): { name: string; addition: MysqlSlowQueryAddition } => {
  const { data } = parseSchema<SlowQuerySchema>(
    slowQuerySchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.SLOW_QUERY,
  );

  return {
    name: '',
    addition: {
      dbType: DB_TYPE.MYSQL,
      startTime: data.time,
      fromTime: data.fromTime,
      toTime: data.toTime,
      instanceId: data.instanceId,
      threadId: data.threadId,
      time: data.time,
      sqlId: data.sqlId,
      sqlTextHashId: data.sqlTextHashId,
      digest: data.digest,
    },
  };
};

export const getMysqlObjectDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: DetailParams): { name: string; addition: MysqlObjectDetailAddition } => {
  const { data } = parseSchema<ObjectDetailSchema>(
    objectDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.OBJECT,
  );

  return {
    name: formatName(data.objectName),
    addition: {
      dbType: DB_TYPE.MYSQL,
      instanceId: data.instanceId,
      objectName: data.objectName,
      databaseName: data.databaseName,
      fromTime: data.fromTime,
      toTime: data.toTime,
    },
  };
};

export const getMysqlParameterDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: ParameterDetailParams): { name: string; addition: MysqlParameterDetailAddition } => {
  const { data } = parseSchema<ParameterDetailSchema>(
    parameterDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.PARAMETER,
  );

  return {
    name: formatName(data.parameterName),
    addition: {
      dbType: DB_TYPE.MYSQL,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
    },
  };
};

export const getMysqlDocumentTextParams = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
  period,
  useFieldValidation,
}: DocumentTextParams) => {
  const formattedTime = formatTimeRange({ fromTime, toTime });
  const timeInfo = period ? { period } : formattedTime;

  if (!useFieldValidation) {
    return {
      instanceId,
      ...timeInfo,
      sqlId: detailInfo.sqlId,
      sqlTextHashId: detailInfo.sqlTextHashId,
    };
  }

  const { data } = parseSchema<DocumentTextSchema>(
    documentTextSchema,
    mergeDetailInfo(detailInfo, instanceId),
    DOCUMENT_TEXT_TYPE,
  );

  return {
    instanceId: data.instanceId,
    ...timeInfo,
    sqlId: data.sqlId,
    sqlTextHashId: data.sqlTextHashId,
  };
};

export const fetchMysqlDocumentText = async ({
  frameName,
  abortController,
  ...rest
}: DocumentTextParams) => {
  const params = getMysqlDocumentTextParams({ ...rest, useFieldValidation: true });

  const {
    data: { data = [] },
  } = await getV7MysqlSqlText(
    params,
    { params: { frameName: frameName || FRAME_NAMES.DOCUMENT_WINDOW.FULL_TEXT } },
    abortController?.signal,
  );

  return data[0] || {};
};

export const getMysqlDocumentTextQueryKey = (params: DocumentTextParams | null) => {
  if (!params) {
    return getGetV7MysqlSqlTextQueryKey();
  }

  const convertedParams = getMysqlDocumentTextParams({ ...params, useFieldValidation: false });

  return getGetV7MysqlSqlTextQueryKey(convertedParams);
};

export const mysqlSqlDetailRequiredMap = getRequiredMap(sqlDetailSchema);
export const mysqlSessionDetailRequiredMap = getRequiredMap(sessionDetailSchema);
export const mysqlSlowQueryRequiredMap = getRequiredMap(slowQuerySchema);
export const mysqlObjectDetailRequiredMap = getRequiredMap(objectDetailSchema);
export const mysqlDocumentTextRequiredMap = getRequiredMap(documentTextSchema);
